/**
 * RoleRedirect — lands the authenticated user on the right surface.
 *
 * Admins → /dashboard. Non-admins are routed by funnel state (review F3 —
 * customers must never dead-end on /unauthorized): a paid order → /account
 * (status stub); otherwise → /start to resume the purchase funnel (the
 * FunnelGuard then picks the exact step).
 */
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';
import { useFunnelState } from '@/hooks/useFunnel';

export function RoleRedirect() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Rendered inside AuthGuard, so the apiClient token getter is wired up.
  const { data: funnelState, isLoading, isError } = useFunnelState({
    enabled: !!user && !isAdmin,
  });

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // On error fall back to the funnel entry — it is safe for every state
  // (FunnelGuard re-resolves the step once the API is reachable again).
  if (isError || !funnelState) {
    return <Navigate to="/start" replace />;
  }

  if (funnelState.latestOrder?.status === 'paid') {
    return <Navigate to="/account" replace />;
  }

  return <Navigate to="/start" replace />;
}
